import { createContext, useCallback, useEffect, useState } from "react";
import { api } from "../services/apiClient";

import {
  AuthProviderType,
  LoginData,
  User,
  AuthState,
  AuthContextData,
} from "../types/authContext";
export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

const ROLES_REQUEST_TIMEOUT_MS = 15000;

const AuthProvider: React.FC<AuthProviderType> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<AuthState>(() => {
    const access_token = localStorage.getItem("@mmeventos:access_token");
    const renew_token = localStorage.getItem("@mmeventos:renew_token");
    const user = localStorage.getItem("@mmeventos:user");

    if (access_token && user && renew_token) {
      api.defaults.headers.common.authorization = `Bearer ${access_token}`;

      return { access_token, renew_token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });
  const [language, setLanguage] = useState("pt");
  const [roleName, setRoleName] = useState("");
  const [accessOptions, setAccessOptions] = useState([]);
  const [actionOptions, setActionOptions] = useState({} as any);

  const createActionOptions = useCallback((moduleArr: any[]) => {
    const currActionOptions = {} as any;
    moduleArr.forEach((module) => {
      const moduleName = module.name;
      const moduleType = module.type;
      Object.keys(moduleType).forEach((action) => {
        currActionOptions[`${action}${moduleName}`] = moduleType[action].active;
      });
    });
    setActionOptions(currActionOptions);
  }, []);

  const handleSetRoleAndActions = useCallback(async () => {
    const access_token = localStorage.getItem("@mmeventos:access_token");
    const userString = localStorage.getItem("@mmeventos:user");
    const user = userString ? JSON.parse(userString) : null;

    if (!access_token || !user?.function_id) {
      setIsLoading(false);
      return;
    }

    api.defaults.headers.common.authorization = `Bearer ${access_token}`;

    try {
      if (user?.function_id) {
        const rolesData = await api
          .get("/roles/get", {
            params: { id: user.function_id },
            timeout: ROLES_REQUEST_TIMEOUT_MS,
          })
          .then((response) => response.data);

        setRoleName(rolesData.name ?? "");
        createActionOptions(rolesData.modules ?? []);
        setAccessOptions(rolesData.modules ?? []);
      }
    } catch (error) {
      console.error("Erro ao carregar permissões:", error);
    } finally {
      setIsLoading(false);
    }
  }, [createActionOptions]);

  const signIn = useCallback(async (loginData: LoginData) => {
    const response = await api.post("/auth/login", loginData);
    const { access_token, renew_token, user } = response.data;
    
    if (access_token !== undefined && user !== undefined) {
      localStorage.setItem("@mmeventos:access_token", access_token);
      localStorage.setItem("@mmeventos:renew_token", renew_token);
      localStorage.setItem("@mmeventos:user", JSON.stringify(user));
      localStorage.setItem("@mmeventos:keep_connected", JSON.stringify(loginData.keep_connected));
      api.defaults.headers.common.authorization = `Bearer ${access_token}`;

      setData({ access_token, renew_token, user });
      handleSetRoleAndActions();
    } else {
      throw new Error("Erro ao fazer login");
    }
  }, [handleSetRoleAndActions]);

  useEffect(() => {
    handleSetRoleAndActions();
  }, [handleSetRoleAndActions]);

  const signOut = useCallback(() => {
    localStorage.removeItem("@mmeventos:access_token");
    localStorage.removeItem("@mmeventos:renew_token");
    localStorage.removeItem("@mmeventos:user");

    setData({} as AuthState);
    setAccessOptions([]);
  }, []);

  const updateUser = useCallback((user: User) => {
    localStorage.setItem("@mmeventos:user", JSON.stringify(user));

    setData((prev) => ({
      ...prev,
      user,
    }));
  }, []);

  const setChosenLanguage = (language: string) => {
    setLanguage(language);
  };

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        userRoleName: roleName,
        accessOptions,
        actionOptions,
        isLoading,
        signIn,
        signOut,
        updateUser,
        language,
        setChosenLanguage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider };
